/**
 * Default configuration defines all piece properties and game mechanics
 *
 * Costs: I=20, L=30, O=15, S=25, T=35
 *
 * Generators (currency production):
 *   - O: Generates 10/sec during active phase (1s active, 2s cooldown)
 *
 * Enhancers (boost adjacent generators):
 *   - I: Boosts adjacent generators by +100% (1x multiplier) when active (1s active, 3s cooldown)
 *
 * Storage Upgrades (increase max currency):
 *   - S: Increases max currency by +1000 per piece (configurable via storageBonus)
 *
 * Cooldown Reducers (reduce neighbors' cooldown duration):
 *   - L: Reduces neighboring pieces' cooldown by 1 second (affected by enhancers)
 *
 * Synchronizers (share effects across network):
 *   - T: Shares effects from I and L pieces across all connected pieces (no state changes)
 *
 * Game cycle: Pieces with non-zero durations automatically cycle between active/cooldown states.
 * Adjacent generators receive stacked boosts from multiple active enhancers.
 * Currency caps at maxCurrency; excess generation is lost.
 * Cooldown reduction is applied before synchronization calculations.
 */
export const DEFAULT_CONFIG = {
    initialCurrency: 0,
    baseMaxCurrency: 1,
    sellRefundRatio: 0.8, // 80% refund when selling pieces or expansions
    tileConfigs: {
        I: { cost: 3, generationRate: 0, activeDuration: 1, cooldownDuration: 4, enhancerMultiplier: 1.0, upgradeCosts: [8, 20, 50, 100, 200, 400, 800], upgradeEffectPerLevel: 0.5 },
        L: { cost: 4, generationRate: 0, activeDuration: 0, cooldownDuration: 0, cooldownReduction: 1.0, upgradeCosts: [6, 18, 45, 90, 180, 360, 720], upgradeEffectPerLevel: 0.5 },
        O: { cost: 0, generationRate: 1, activeDuration: 1, cooldownDuration: 2, upgradeCosts: [5, 15, 35, 75, 150, 300, 600], upgradeEffectPerLevel: 0.5 },
        S: { cost: 1, generationRate: 0, activeDuration: 0, cooldownDuration: 0, storageBonus: 2, upgradeCosts: [3, 10, 25, 50, 100, 200, 400], upgradeEffectPerLevel: 1 },
        T: { cost: 5, generationRate: 0, activeDuration: 0, cooldownDuration: 0, conductingFactor: 1, upgradeCosts: [10, 25, 60, 120, 240, 480, 960], upgradeEffectPerLevel: 0.25 },
    },
    // Tax, rent, and expansion cost per map size and prestige level. Keys are "WxHxP". All unlisted sizes default to { tax: 0, rent: 0, expansionCost: 0 }.
    mapSizeConfigs: {
        // Prestige level 1
        '4x4x1': { tax: 0, rent: 0, expansionCost: 0 },
        '4x6x1': { tax: 0, rent: 0, expansionCost: 3 },
        '6x4x1': { tax: 0, rent: 0, expansionCost: 3 },
        '6x6x1': { tax: 1, rent: 1, expansionCost: 5 },
        '4x8x1': { tax: 1, rent: 0, expansionCost: 7 },
        '8x4x1': { tax: 1, rent: 0, expansionCost: 7 },
        '6x8x1': { tax: 1, rent: 1, expansionCost: 9 },
        '8x6x1': { tax: 1, rent: 1, expansionCost: 9 },
        '8x8x1': { tax: 1, rent: 2, expansionCost: 21, prestigeCost: 50 },
        // Prestige level 2
        '4x4x2': { tax: 0, rent: 0, expansionCost: 0 },
        '4x6x2': { tax: 1, rent: 1, expansionCost: 5 },
        '6x4x2': { tax: 1, rent: 1, expansionCost: 5 },
        '6x6x2': { tax: 2, rent: 2, expansionCost: 8 },
        '4x8x2': { tax: 2, rent: 1, expansionCost: 11 },
        '8x4x2': { tax: 2, rent: 1, expansionCost: 11 },
        '6x8x2': { tax: 2, rent: 2, expansionCost: 14 },
        '8x6x2': { tax: 2, rent: 2, expansionCost: 14 },
        '8x8x2': { tax: 2, rent: 3, expansionCost: 32, prestigeCost: 200 },
    },
};
/**
 * Tick rate configuration
 * Can be changed later to speed up/slow down the game
 */
export const TICK_RATE = 5; // ticks per second
export const TICK_DELTA = 1 / TICK_RATE; // seconds per tick
export const KEYBINDINGS = {
    rotateCCW: 'q',
    rotateCW: 'e',
    mirror: 'r',
    selectPiece: ['1', '2', '3', '4', '5'],
    moveUp: 'w',
    moveDown: 's',
    moveLeft: 'a',
    moveRight: 'd',
    place: ' ',
};
//# sourceMappingURL=config.js.map